/**
 * @file index.jsx
 * @brief Displays a listing detail page with gallery, metadata, moderation actions, and seller contact panel.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { createApiClient } from "../../api/axios";
import { useUserProfile } from "../../context/UserProfileContext";
import useListingDetails from "../../hooks/useListingDetails";
import ContactSeller from "../ContactSeller";
import { useTranslation } from "react-i18next";
import { getUserDisplayName } from "../../utils/userDisplayName";
import styles from "./listing-details.module.css";
import DOMPurify from "dompurify";

/* =========================
   STATUS CONFIG
========================= */

const STATUS_CONFIG = {
    draft:    { key: "listingDetails.statuses.draft",   color: "statusDraft" },
    pend_app: { key: "listingDetails.statuses.pending", color: "statusPending" },
    active:   { key: "listingDetails.statuses.active",  color: "statusActive" },
    transaction_in_progress: { key: "listingDetails.statuses.transactionInProgress", color: "statusTransaction" },
    paused:   { key: "listingDetails.statuses.paused",  color: "statusPaused" },
    sold:     { key: "listingDetails.statuses.sold",    color: "statusSold" },
    rejected: { key: "listingDetails.statuses.rejected", color: "statusRejected" },
};

/* =========================
   ACTIONS
========================= */

function ListingActions({ listing, profile, onUpdateStatus, onDelete, t }) {
    const isAdmin     = profile?.groups?.includes("admins");
    const isModerator = profile?.groups?.includes("moderators");
    const isAuthor    = listing.author?.id === profile?.id;
    const sellerLocked = listing.status === "transaction_in_progress" && isAuthor && !isAdmin && !isModerator;

    if (!isAdmin && !isModerator && !isAuthor) return null;

    if (sellerLocked) {
        return (
            <div className={styles.managementRow}>
                <span className={styles.lockedText}>{t("listingDetails.transactionLocked")}</span>
            </div>
        );
    }

    return (
        <div className={styles.managementRow}>
            <details className={styles.actionsMenu}>
                <summary className={styles.actionsMenuTrigger}>
                    {t("listingDetails.actions.manageListing")}
                    <span aria-hidden="true">▾</span>
                </summary>

                <div className={styles.actionsMenuContent}>
                    <Link to={`/listings/${listing.id}/edit`} className={styles.menuItemLink}>
                        {t("common.edit")}
                    </Link>

                    {listing.status === "draft" && (
                        <button className={styles.menuItem} onClick={() => onUpdateStatus("pend_app")}>
                            {t("listingDetails.actions.submitReview")}
                        </button>
                    )}
                    {listing.status === "active" && (
                        <button className={styles.menuItem} onClick={() => onUpdateStatus("paused")}>
                            {t("listingDetails.actions.pause")}
                        </button>
                    )}
                    {listing.status === "paused" && (
                        <button className={styles.menuItem} onClick={() => onUpdateStatus("active")}>
                            {t("listingDetails.actions.resume")}
                        </button>
                    )}
                    {listing.status === "active" && (
                        <button className={styles.menuItem} onClick={() => onUpdateStatus("sold")}>
                            {t("listingDetails.actions.markSold")}
                        </button>
                    )}

                    {(isAdmin || isModerator) && (
                        <>
                            {listing.status !== "active" && (
                                <button className={styles.menuItem} onClick={() => onUpdateStatus("active")}>
                                    {t("listingDetails.actions.approve")}
                                </button>
                            )}
                            {listing.status !== "rejected" && (
                                <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={() => onUpdateStatus("rejected")}>
                                    {t("listingDetails.actions.reject")}
                                </button>
                            )}
                        </>
                    )}

                    <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={onDelete}>
                        {t("listingDetails.actions.delete")}
                    </button>
                </div>
            </details>
        </div>
    );
}

/* =========================
   MAIN
========================= */

export default function ListingDetail() {
    const { t, i18n } = useTranslation();
    const { id }      = useParams();
    const navigate    = useNavigate();
    const auth        = useAuth();
    const api         = useMemo(() => createApiClient(auth), [auth]);
    const { profile } = useUserProfile();

    const [activeIndex, setActiveIndex]   = useState(0);
    const [viewerOpen, setViewerOpen]     = useState(false);
    const [viewerIndex, setViewerIndex]   = useState(0);
    const [interestOpen, setInterestOpen] = useState(true);
    const locale = i18n.resolvedLanguage?.startsWith("fr") ? "fr-FR" : "en-US";

    const {
        listing,
        images,
        loading,
        error,
        toggleFavorite,
        updateStatus,
        deleteListing,
    } = useListingDetails({
        api,
        listingId: id,
        navigate,
    });

    /* ── Keyboard navigation in lightbox ── */
    useEffect(() => {
        if (!viewerOpen) return;
        /**
         * Handles lightbox keyboard shortcuts while the expanded gallery is open.
         * @param {KeyboardEvent} e The keyboard event raised on the window.
         * @returns {void}
         */
        const handler = (e) => {
            if (e.key === "Escape")      setViewerOpen(false);
            if (e.key === "ArrowRight")  setViewerIndex((i) => (i + 1) % images.length);
            if (e.key === "ArrowLeft")   setViewerIndex((i) => (i - 1 + images.length) % images.length);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [viewerOpen, images.length]);

    useEffect(() => {
        setActiveIndex(0);
        setViewerIndex(0);
    }, [listing?.id, images.length]);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 768px)");
        const syncInterestPanel = (evt) => setInterestOpen(!evt.matches);

        setInterestOpen(!mediaQuery.matches);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", syncInterestPanel);
            return () => mediaQuery.removeEventListener("change", syncInterestPanel);
        }

        mediaQuery.addListener(syncInterestPanel);
        return () => mediaQuery.removeListener(syncInterestPanel);
    }, []);

    /* ── Actions ── */
    const handleDelete = async () => {
        if (!window.confirm(t("listingDetails.deleteConfirm"))) return;
        await deleteListing();
    };

    /* ── States ── */
    if (loading) return (
        <div className={styles.stateCenter}>
            <div className={styles.spinner} />
            <p>{t("listingDetails.loading")}</p>
        </div>
    );
    if (error)    return <div className={styles.stateCenter}>⚠️ {t("listingDetails.error")}</div>;
    if (!listing) return <div className={styles.stateCenter}>{t("listingDetails.notFound")}</div>;

    const statusCfg = STATUS_CONFIG[listing.status] || {};
    const sellerName = getUserDisplayName(listing.author?.user);

    /* ── Render ── */
    return (
        <div className={styles.container}>

            <button className={styles.back} onClick={() => navigate(-1)}>
                ← {t("listingDetails.actions.back")}
            </button>

            <div className={styles.card}>

                {/* ── LEFT: Gallery ── */}
                <div className={styles.gallery}>

                    <div className={styles.mainImageWrapper}>
                        {/* Favorite pill */}
                        {auth.isAuthenticated && (
                        <div className={styles.favoriteOverlay}>
                            <button
                                className={`${styles.favoriteBtn} ${listing.is_favorited ? styles.favorited : ""}`}
                                onClick={toggleFavorite}
                                aria-label={listing.is_favorited ? t("listingDetails.removeFromFavorites") : t("listingDetails.addToFavorites")}
                            >
                                {listing.is_favorited ? "❤️" : "🤍"}
                            </button>
                            <span className={styles.favoriteCount}>{listing.favorites_count}</span>
                        </div>
                        )}

                        {images.length > 0 ? (
                            <>
                                <img
                                    src={images[activeIndex]}
                                    className={styles.mainImage}
                                    alt={listing.title}
                                    onClick={() => { setViewerIndex(activeIndex); setViewerOpen(true); }}
                                />
                                <span className={styles.zoomHint}>🔍 {t("listingDetails.clickToEnlarge")}</span>
                            </>
                        ) : (
                            <div className={styles.noImage}>{t("common.noImage")}</div>
                        )}
                    </div>

                    {images.length > 1 && (
                        <div className={styles.thumbnails}>
                            {images.map((img, i) => (
                                <img
                                    key={i}
                                    src={img}
                                    onClick={() => setActiveIndex(i)}
                                    className={`${styles.thumb} ${i === activeIndex ? styles.thumbActive : ""}`}
                                    alt={`Thumbnail ${i + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* ── RIGHT: Info ── */}
                <div className={styles.info}>

                    <div className={styles.topMetaRow}>
                        {listing.category?.name && (
                            <span className={`${styles.topMetaChip} ${styles.categoryChip}`}>🏷 {listing.category.name}</span>
                        )}
                        <span className={`${styles.topMetaChip} ${styles.locationChip}`}>
                            📍 {listing.location?.name || t("common.unknown")}
                        </span>
                        <span className={styles.topMetaChip}>📅 {new Date(listing.created_at).toLocaleDateString(locale)}</span>
                    </div>

                    <div className={styles.titleRow}>
                        <div className={styles.titleStack}>
                            <h1 className={styles.title}>{listing.title}</h1>
                            <p className={styles.subtitle}>
                                {t("listingDetails.byAuthor", { name: sellerName.primaryName })}
                                <span className={styles.subtitleDivider} aria-hidden="true">•</span>
                                <Link to={`/profile/${listing.author?.id}`} className={styles.authorLinkInline}>
                                    {t("header.profile")}
                                </Link>
                            </p>
                        </div>

                        <div className={styles.titleAside}>
                            <span className={`${styles.statusBadge} ${styles[statusCfg.color]}`}>
                                {t(statusCfg.key)}
                            </span>
                            <div className={styles.price}>{listing.price} €</div>
                        </div>
                    </div>

                    <ListingActions
                        listing={listing}
                        profile={profile}
                        onUpdateStatus={updateStatus}
                        onDelete={handleDelete}
                        t={t}
                    />

                    <div
                        className={styles.description}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(listing.description || "") }}
                    />

                    <div className={styles.divider} />
                    {auth.isAuthenticated && listing.author?.id !== profile?.id && (
                    <details
                        className={styles.interestPanel}
                        open={interestOpen}
                        onToggle={(e) => setInterestOpen(e.currentTarget.open)}
                    >
                        <summary className={styles.interestPanelSummary}>
                            {t("listingDetails.actions.contactAndOffer")}
                            <span aria-hidden="true">▾</span>
                        </summary>
                        <div className={styles.interestPanelBody}>
                            <ContactSeller listing={listing} compact />
                        </div>
                    </details>
                    )}

                    <div className={styles.metaGrid}>
                        <div className={styles.metaItem}>
                            <span className={styles.metaIcon}>📍</span>
                            <span>{listing.location?.name || t("common.unknown")}</span>
                        </div>
                        <div className={styles.metaItem}>
                            <span className={styles.metaIcon}>👤</span>
                            <Link to={`/profile/${listing.author?.id}`} className={styles.authorLink}>
                                {sellerName.primaryName}
                            </Link>
                        </div>
                        {listing.category?.name && (
                            <div className={styles.metaItem}>
                                <span className={styles.metaIcon}>🏷</span>
                                <span>{listing.category.name}</span>
                            </div>
                        )}
                        <div className={styles.metaItem}>
                            <span className={styles.metaIcon}>📅</span>
                            <span>{new Date(listing.created_at).toLocaleDateString(locale)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Lightbox ── */}
            {viewerOpen && (
                <div className={styles.viewerOverlay} onClick={() => setViewerOpen(false)}>
                    <button className={styles.viewerClose} onClick={() => setViewerOpen(false)}>✕</button>

                    {images.length > 1 && (
                        <>
                            <button className={styles.viewerPrev} onClick={(e) => { e.stopPropagation(); setViewerIndex((i) => (i - 1 + images.length) % images.length); }}>
                                ‹
                            </button>
                            <button className={styles.viewerNext} onClick={(e) => { e.stopPropagation(); setViewerIndex((i) => (i + 1) % images.length); }}>
                                ›
                            </button>
                        </>
                    )}

                    <img
                        src={images[viewerIndex]}
                        className={styles.viewerImage}
                        alt={listing.title}
                        onClick={(e) => e.stopPropagation()}
                    />

                    {images.length > 1 && (
                        <div className={styles.viewerDots}>
                            {images.map((_, i) => (
                                <button
                                    key={i}
                                    className={`${styles.dot} ${i === viewerIndex ? styles.dotActive : ""}`}
                                    onClick={(e) => { e.stopPropagation(); setViewerIndex(i); }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
